import React, { forwardRef, useImperativeHandle } from "react";
import {
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Drawer,
    useRestoreFocusSource,
    useRestoreFocusTarget,
} from "@fluentui/react-components";

import { Dismiss24Regular, PanelLeft16Regular, Bot20Regular, Bot24Regular, Settings24Regular } from "@fluentui/react-icons";
import { GetAssetService } from "../services/AssetService";
import { PiAsset } from "../Types/PiAsset";
import AssistantDashboard from "../dashboards/AssistantDashboard";
import QuickAssistant from "../dashboards/QuickAssistant";
import HistoryDrawerPilotSide from "./Assistant/HistoryDrawerPilotSide";
import { AssistantChatSide } from "./Assistant/AssistantChatSide";
import { newChatSession, postChatMessage, getSessionList } from "../services/AssistantService";
import { userId } from "../App";
import { useUserPreferences } from "../contexts/UserPreferencesContext";

interface AssetDrawerProps {
    className?: string;
    startOpen: boolean;
    initialPrompt?: string;
}

export interface PilotDrawerRef {
    openWithPrompt: (prompt: string) => void;
    openWithoutPrompt: () => void;
    toggle: () => void;
}

const PilotDrawer = forwardRef<PilotDrawerRef, AssetDrawerProps>((props, ref) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [asset, setAssets] = React.useState<PiAsset>();
    const [isMobile, setIsMobile] = React.useState(false);

    const [sessionId, setSessionId] = React.useState<number>(-1);
    const [isHistoryOpen, setIsHistoryOpen] = React.useState(false);
    const [currentPrompt, setCurrentPrompt] = React.useState<string>("");
    const { openSettings } = useUserPreferences();

    // Get previously opened session from localStorage on component mount
    const [previousSessionId, setPreviousSessionId] = React.useState<number>(() => {
        const stored = localStorage.getItem('pilot-drawer-last-session');
        const result = stored ? parseInt(stored, 10) : -1;
        console.log('Component initialized, localStorage value:', stored, 'parsed to:', result);
        return result;
    });

    const setSessionState = (sessionId: number) => {
        console.log('setSessionState called with sessionId:', sessionId);
        setSessionId(sessionId);
        setPreviousSessionId(sessionId); // Track the session that was opened
        // Persist to localStorage
        localStorage.setItem('pilot-drawer-last-session', sessionId.toString());
        console.log('Updated localStorage with sessionId:', sessionId);
    }

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        openWithPrompt: async (prompt: string) => {
            setCurrentPrompt(prompt);
            setIsOpen(true);

            // Create a new session
            try {
                const sessionResponse = await newChatSession(userId);
                setSessionId(sessionResponse);
                setPreviousSessionId(sessionResponse); // Track this new session
                localStorage.setItem('pilot-drawer-last-session', sessionResponse.toString());
                // Clear the prompt after setting session to prevent re-sending
                setTimeout(() => setCurrentPrompt(""), 2000);
            } catch (error) {
                console.error("Failed to create new session:", error);
            }
        },
        openWithoutPrompt: async () => {
            setCurrentPrompt("");
            setIsOpen(true);

            // Create a new session
            try {
                const sessionResponse = await newChatSession(userId);
                setSessionId(sessionResponse);
                setPreviousSessionId(sessionResponse); // Track this new session
                localStorage.setItem('pilot-drawer-last-session', sessionResponse.toString());
            } catch (error) {
                console.error("Failed to create new session:", error);
            }
        },
        toggle: async () => {
            if (isOpen) {
                setIsOpen(false);
            } else {
                setCurrentPrompt("");
                setIsOpen(true);

                console.log('Toggle called, previousSessionId:', previousSessionId);
                console.log('localStorage value:', localStorage.getItem('pilot-drawer-last-session'));

                // The HistoryDrawerPilotSide will handle loading the preferred session
                // We just need to make sure we don't override it here
            }
        }
    }));

    // Overlay Drawer will handle focus by default, but inline Drawers need manual focus restoration attributes, if applicable
    const restoreFocusTargetAttributes = useRestoreFocusTarget();
    const restoreFocusSourceAttributes = useRestoreFocusSource();

    React.useEffect(() => { setIsOpen(props.startOpen) }, [props.startOpen]);

    // Debug useEffect to track previousSessionId changes
    React.useEffect(() => {
        console.log('previousSessionId changed to:', previousSessionId);
    }, [previousSessionId]);

    // Debug useEffect to track sessionId changes
    React.useEffect(() => {
        console.log('sessionId changed to:', sessionId);
    }, [sessionId]);

    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    React.useEffect(() => {
        (async () => {
            const assets = await GetAssetService();
            setAssets(assets);

        })();
    }, []);

    const drawerWidth = isMobile ? "100vw" : "25vw";
    const buttonWidth = "24px";

    return (
        <div className={`${props.className} fixed top-0 right-0 z-50 h-full pointer-events-none drop-shadow-md`}>
            <div className="flex h-full items-end flex-col pointer-events-auto">
                {/* Toggle Button */}
                {!isOpen && (
                    <div className="h-full w-6 flex flex-col">
                        <button
                            type="button"
                            aria-label="Settings"
                            title="Settings"
                            className="w-6 shrink-0 bg-primary-light dark:bg-primary-dark hover:bg-opacity-75 flex justify-center pt-2 pb-1"
                            onClick={openSettings}
                        >
                            <Settings24Regular className="text-text-light dark:text-text-dark" />
                        </button>
                        <button
                            className="flex-1 w-6 bg-primary-light dark:bg-primary-dark hover:bg-opacity-75 flex justify-center items-start pt-1"
                            aria-label="Open pilOT"
                            {...restoreFocusTargetAttributes}
                            onClick={() => setIsOpen(true)}
                        >
                            <Bot24Regular className="text-text-light dark:text-text-dark" />
                        </button>
                    </div>
                )}
                <a
                    href="https://forms.office.com/e/MMkwf4DYzZ"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-28 h-6 text-center fixed bottom-20 -right-11 -rotate-90 "
                >
                    <button className="w-full h-full flex items-center justify-center text-text-light dark:text-text-dark text-base">
                        Feedback
                    </button>
                </a>

                {/* Drawer Container */}
                {isOpen && (
                    <div className={`h-full transition-all duration-300 ease-in-out ${isMobile ? 'w-screen' : 'w-[50vw]'}`}>
                        <Drawer
                            {...restoreFocusSourceAttributes}
                            type="overlay"
                            separator
                            open={isOpen}
                            onOpenChange={(_, { open }) => setIsOpen(open)}
                            position="end"
                            style={{
                                height: "100%",
                                width: isMobile ? "100vw" : "35vw"
                            }}
                        >
                            <DrawerHeader>
                                <DrawerHeaderTitle
                                    action={
                                        <div className="flex items-center gap-2">

                                            <button
                                                aria-label="Close"
                                                onClick={() => setIsOpen(false)}
                                            >
                                                <Dismiss24Regular />
                                            </button>
                                        </div>
                                    }
                                >
                                    pilOT
                                </DrawerHeaderTitle>
                            </DrawerHeader>

                            <DrawerBody className="relative p-0 overflow-hidden">
                                {/* Main Chat Area - Full height and width */}
                                <div className="h-full w-full 5">
                                    {sessionId !== -1 ? (
                                        <AssistantChatSide sessionId={sessionId} initialPrompt={currentPrompt} />
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-gray-500">
                                            <p>Select a chat from history or start a new conversation</p>
                                        </div>
                                    )}
                                </div>

                                {/* History Drawer - Overlays on top of chat */}
                                <div className="absolute top-0 left-0 h-full z-10">
                                    <HistoryDrawerPilotSide
                                        startOpen={isHistoryOpen}
                                        setSessionState={setSessionState}
                                        preferredSessionId={previousSessionId !== -1 ? previousSessionId : undefined}
                                    />
                                </div>
                            </DrawerBody>
                        </Drawer>
                    </div>
                )}
            </div>
        </div>

    );
});

PilotDrawer.displayName = 'PilotDrawer';

export default PilotDrawer;